/**
 * Donation form suggested-amount picker.
 *
 * Paired with templates/_molecules/donationSuggestions.twig, which Formie renders inside
 * the donation form's `donationSuggestions` HTML field.
 */
import { findField, readField, writeField } from './donation-formie-fields'
;(() => {
    const CONTAINER = '.donation-suggestions'
    const SET = '.donation-suggestions__set'
    const CHIP = '.donation-suggestion'

    const SELECTED_CLASS = 'donation-suggestion--selected'
    const ACTIVE_CLASS = 'donation-amount--active'

    const FIELD_AMOUNT = 'donationAmount'
    const FIELD_FREQUENCY = 'donationFrequency'

    const setClass = (key) => `donation-suggestions__set--${key}`

    /**
     * The chip's amount, in integer cents. Read off the attribute rather than the `value` property
     */
    const chipCents = (chip) => parseInt(chip?.getAttribute('value') ?? '', 10)

    /**
     * Formats cents for the donation amount box.
     */
    const formatAmount = (cents) =>
        cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2)

    /**
     * Reads `donationAmount` as integer cents. Returns null for anything that isn't a usable number, so "no amount yet" and "$0" stay distinguishable.
     */
    const readAmountCents = (form) => {
        const raw = readField(form, FIELD_AMOUNT)

        if (raw === null || String(raw).trim() === '') {
            return null
        }

        const parsed = Number(String(raw).replace(/[^0-9.-]/g, ''))

        return Number.isFinite(parsed) ? Math.round(parsed * 100) : null
    }

    /**
     * Normalises the frequency to the modifier used on the set wrappers.
     */
    const readFrequency = (form) =>
        (readField(form, FIELD_FREQUENCY) ?? 'once').trim().toLowerCase()

    /**
     * Shows the set matching the current frequency and hides the rest.
     */
    const showSet = (container, frequency) => {
        const sets = Array.from(container.querySelectorAll(SET))
        const matched = sets.some((set) => set.classList.contains(setClass(frequency)))

        sets.forEach((set) => {
            set.hidden = matched && !set.classList.contains(setClass(frequency))
        })
    }

    /**
     * The chips of whichever set is currently on screen.
     */
    const visibleChips = (container) =>
        Array.from(container.querySelectorAll(SET))
            .filter((set) => !set.hidden)
            .flatMap((set) => Array.from(set.querySelectorAll(CHIP)))

    /**
     * Marks the chip matching the current amount, in the visible set only. A $50 chip can
     * exist under both frequencies, and pressing the hidden one would be a lie.
     *
     * Also flags the amount input itself as "active" - but only when the amount is a
     * genuinely custom one (no chip matches it). When a chip does match, that chip already
     * shows the active state, so the input shouldn't double up on the same treatment.
     */
    const markSelected = (container, cents, amountInput) => {
        let hasMatch = false

        container.querySelectorAll(CHIP).forEach((chip) => {
            const set = chip.closest(SET)
            const isVisible = !set || !set.hidden
            const isMatch = isVisible && cents !== null && chipCents(chip) === cents

            if (isMatch) {
                hasMatch = true
            }

            chip.setAttribute('aria-pressed', isMatch ? 'true' : 'false')
            chip.classList.toggle(SELECTED_CLASS, isMatch)
        })

        amountInput?.classList.toggle(ACTIVE_CLASS, cents !== null && !hasMatch)
    }

    /**
     * Position of the pressed chip within the visible set, or -1.
     */
    const selectedIndex = (container) =>
        visibleChips(container).findIndex((chip) => chip.getAttribute('aria-pressed') === 'true')

    const isFrequencyTarget = (target) =>
        Boolean(target.closest?.(`[data-field-handle="${FIELD_FREQUENCY}"]`)) ||
        target.name === `fields[${FIELD_FREQUENCY}]`

    const init = () => {
        document.querySelectorAll(CONTAINER).forEach((container) => {
            if (container.dataset.donationAmountReady) {
                return
            }

            const form = container.closest('form')

            if (!form) {
                console.error(
                    '[donationAmount] `.donation-suggestions` is not inside a <form>, so the picker is inert.',
                    container
                )

                return
            }

            container.dataset.donationAmountReady = 'true'

            const chips = Array.from(container.querySelectorAll(CHIP))

            if (!chips.length) {
                console.error(
                    `[donationAmount] No "${CHIP}" buttons found. If the HTML field has "Purify Content" on, turn it off — HTMLPurifier drops <button> and its attributes.`,
                    container
                )
            } else {
                const broken = chips.filter((chip) => !Number.isFinite(chipCents(chip)))

                if (broken.length) {
                    console.error(
                        `[donationAmount] ${broken.length} of ${chips.length} chips have no usable "value" attribute, so clicking them does nothing. Again, check "Purify Content".`,
                        broken
                    )
                }
            }

            if (!findField(form, FIELD_AMOUNT)) {
                console.error(
                    `[donationAmount] No "${FIELD_AMOUNT}" field in this form — chips will highlight but nothing will be charged.`
                )
            }

            if (!findField(form, FIELD_FREQUENCY)) {
                console.warn(
                    `[donationAmount] No "${FIELD_FREQUENCY}" field in this form — the Once set will stay visible and never swap.`
                )
            }

            // Whether the amount in the field was put there by a chip rather than typed.
            let chipDerived = false

            // True only for the instant we're writing the field ourselves.
            let writingSelf = false

            const setAmountCents = (cents) => {
                writingSelf = true
                writeField(form, FIELD_AMOUNT, formatAmount(cents), 'donationAmount')
                writingSelf = false

                chipDerived = true
            }

            const amountInput = findField(form, FIELD_AMOUNT)

            // The "$" prefix and padding around the input are part of the same
            // clickable-looking box, so clicking anywhere in it should focus the
            // input rather than only the digits themselves.
            const amountWrapper = amountInput?.closest('.fui-input-wrapper')

            amountWrapper?.addEventListener('click', (event) => {
                if (event.target !== amountInput) {
                    amountInput.focus()
                }
            })

            container.addEventListener('click', (event) => {
                const chip = event.target.closest(CHIP)

                if (!chip || !container.contains(chip)) {
                    return
                }

                const cents = chipCents(chip)

                if (!Number.isFinite(cents)) {
                    return
                }

                setAmountCents(cents)
                markSelected(container, cents, amountInput)
            })

            if (amountInput) {
                // Typing a custom amount takes ownership away from the chips.
                amountInput.addEventListener('input', () => {
                    if (writingSelf) {
                        return
                    }

                    chipDerived = false
                    markSelected(container, readAmountCents(form), amountInput)
                })
            }

            form.addEventListener('change', (event) => {
                if (!isFrequencyTarget(event.target)) {
                    return
                }

                // Carrying a $500 one-off into "Give monthly" would quietly set up a $500/month subscription. Rather than clearing the field
                const previousIndex = chipDerived ? selectedIndex(container) : -1

                showSet(container, readFrequency(form))

                if (previousIndex >= 0) {
                    const chips = visibleChips(container)
                    const chip = chips[Math.min(previousIndex, chips.length - 1)]
                    const cents = chipCents(chip)

                    if (Number.isFinite(cents)) {
                        setAmountCents(cents)
                    }
                }

                markSelected(container, readAmountCents(form), amountInput)
            })

            // Sync once on load
            showSet(container, readFrequency(form))
            markSelected(container, readAmountCents(form), amountInput)
        })
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init)
    } else {
        init()
    }
})()
